// Cart drawer additions for Impact: instant open on add to cart, quantity buttons, dynamic upsells, one-click upsell
// add, and the add-on (Gift Package) toggle.
// Every cart update goes through the theme's own "cart:change" event so the drawer and the header cart count
// re-render exactly like a regular add to cart.

const routeRoot = () => window.Shopify?.routes?.root || '/';
const recommendationsCache = new Map();
const RECOMMENDATIONS_STORAGE_KEY = 'cart-drawer-plus:recommendations';
const RECOMMENDATIONS_MAX_AGE = 10 * 60 * 1000;

function readStoredRecommendations() {
  try {
    return JSON.parse(sessionStorage.getItem(RECOMMENDATIONS_STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

// Prices depend on the market, so cached upsells are kept per product, country, currency and language
function recommendationsKey(productId) {
  const { country, currency, locale } = window.Shopify || {};
  return [productId, country, currency?.active, locale].join(':');
}

function storeRecommendations(key, html) {
  try {
    const stored = readStoredRecommendations();
    stored[key] = { html, time: Date.now() };
    sessionStorage.setItem(RECOMMENDATIONS_STORAGE_KEY, JSON.stringify(stored));
  } catch (error) {
    // Storage can be full or blocked: the in-memory cache still works
  }
}

// Returns a promise of the recommendations section HTML for a product. Results are cached in memory and in
// sessionStorage, so the upsells show instantly when the drawer re-renders or on the next page.
function fetchRecommendations(productId) {
  if (!productId) return Promise.resolve('');

  const key = recommendationsKey(productId);

  if (!recommendationsCache.has(key)) {
    const stored = readStoredRecommendations()[key];

    if (stored && Date.now() - stored.time < RECOMMENDATIONS_MAX_AGE) {
      recommendationsCache.set(key, Promise.resolve(stored.html));
    } else {
      const url = `${routeRoot()}recommendations/products?product_id=${productId}&limit=10&intent=related&section_id=cart-recommendations`;

      recommendationsCache.set(key, fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (html) storeRecommendations(key, html);
          return html;
        })
        .catch(() => ''));
    }
  }

  return recommendationsCache.get(key);
}

async function updateCart(endpoint, payload) {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

  const response = await fetch(`${routeRoot()}cart/${endpoint}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...payload, sections: sections.join(',') })
  });

  const responseJson = await response.json();

  if (!response.ok) {
    throw new Error(responseJson.description || responseJson.message || 'Error');
  }

  // /cart/add.js only returns the added items, the other endpoints return the full cart
  const cart = endpoint === 'add' ? await (await fetch(`${routeRoot()}cart.js`)).json() : responseJson;
  cart.sections = responseJson.sections;

  document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart-drawer-plus:change', cart } }));
  return cart;
}

class CartUpsellItem extends HTMLElement {
  connectedCallback() {
    this.variantInput = this.querySelector('[data-variant-input]');
    this.addButton = this.querySelector('[data-add-button]');

    if (this.variantInput?.tagName === 'SELECT') {
      this.variantInput.addEventListener('change', () => {
        this.updatePrice();
        this.fitSelect();
      });

      this.fitSelect();
      document.fonts?.ready.then(() => this.fitSelect());
    }

    this.addButton?.addEventListener('click', () => this.add());
  }

  // Browsers size a select to its longest option, so fit it to the option that is selected instead
  fitSelect() {
    const select = this.variantInput;
    const style = getComputedStyle(select);
    const context = (CartUpsellItem.measureCanvas ??= document.createElement('canvas')).getContext('2d');

    context.font = style.font;

    const textWidth = context.measureText(select.selectedOptions[0]?.text.trim() || '').width;
    const extraWidth = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'].reduce((sum, property) => sum + (parseFloat(style[property]) || 0), 0);

    select.style.width = `${Math.ceil(textWidth + extraWidth)}px`;
  }

  updatePrice() {
    const option = this.variantInput.selectedOptions[0];
    const price = this.querySelector('[data-price]');
    const comparePrice = this.querySelector('[data-compare-price]');

    if (!option) return;

    price.innerHTML = option.dataset.price;
    comparePrice.innerHTML = option.dataset.comparePrice || '';
    comparePrice.hidden = !option.dataset.comparePrice;
  }

  async add() {
    if (this.addButton.getAttribute('aria-busy') === 'true') return;

    this.addButton.setAttribute('aria-busy', 'true');
    this.querySelector('[role="alert"]')?.remove();

    try {
      await updateCart('add', { items: [{ id: Number(this.variantInput.value), quantity: 1 }] });
    } catch (error) {
      this.addButton.removeAttribute('aria-busy');
      const alert = document.createElement('p');
      alert.className = 'cdp-error';
      alert.setAttribute('role', 'alert');
      alert.textContent = error.message;
      this.appendChild(alert);
    }
  }
}

class CartUpsells extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.cdp-upsells__list');
    this.limit = parseInt(this.getAttribute('limit')) || 6;

    Array.from(this.list.children).slice(this.limit).forEach((item) => item.remove());
    this.refreshVisibility();

    if (this.hasAttribute('product-id')) {
      this.loadRecommendations();
    }
  }

  async loadRecommendations() {
    const html = await fetchRecommendations(this.getAttribute('product-id'));

    if (!html || !this.isConnected) return;

    const excluded = new Set((this.getAttribute('exclude') || '').split(',').filter(Boolean));
    this.list.querySelectorAll('[data-product-id]').forEach((item) => excluded.add(item.dataset.productId));

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const addLabel = this.getAttribute('add-label');

    for (const item of fragment.querySelectorAll('cart-upsell-item')) {
      if (this.list.children.length >= this.limit) break;
      if (excluded.has(item.dataset.productId)) continue;

      if (addLabel) {
        item.querySelector('[data-add-button]').textContent = addLabel;
      }

      excluded.add(item.dataset.productId);
      this.list.appendChild(document.importNode(item, true));
    }

    this.refreshVisibility();
  }

  refreshVisibility() {
    this.hidden = this.list.children.length === 0;
  }
}

class CartAddonToggle extends HTMLElement {
  connectedCallback() {
    this.switchButton = this.querySelector('[role="switch"]');
    this.switchButton.addEventListener('click', () => this.toggle());
  }

  async toggle() {
    if (this.hasAttribute('aria-busy')) return;

    const isChecked = this.switchButton.getAttribute('aria-checked') === 'true';
    const lineKey = this.getAttribute('line-key');

    this.setAttribute('aria-busy', 'true');
    this.switchButton.setAttribute('aria-checked', String(!isChecked));

    try {
      if (isChecked && lineKey) {
        await updateCart('change', { id: lineKey, quantity: 0 });
      } else {
        await updateCart('add', { items: [{ id: Number(this.getAttribute('variant-id')), quantity: 1 }] });
      }
    } catch (error) {
      this.switchButton.setAttribute('aria-checked', String(isChecked));
      this.removeAttribute('aria-busy');
    }
  }
}

function showLineError(line, message) {
  line.querySelector('[role="alert"]')?.remove();

  const alert = document.createElement('p');
  alert.className = 'cdp-error';
  alert.setAttribute('role', 'alert');
  alert.textContent = message;
  line.querySelector('.cdp-line__info').appendChild(alert);
}

// Quantity buttons and the remove icon. A merged row stands for several cart lines, so its remove icon removes all
// of them, and its +/- buttons change one of them by one step.
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('.cdp-line [data-line-change], .cdp-line [data-remove-keys]');
  if (!button) return;

  const line = button.closest('.cdp-line');
  if (line.getAttribute('aria-busy') === 'true') return;

  line.setAttribute('aria-busy', 'true');

  const request = button.hasAttribute('data-remove-keys')
    ? updateCart('update', { updates: Object.fromEntries(button.dataset.removeKeys.split(',').map((key) => [key, 0])) })
    : updateCart('change', { id: button.dataset.lineChange, quantity: parseInt(button.dataset.quantity) });

  request.catch((error) => {
    line.removeAttribute('aria-busy');
    showLineError(line, error.message);
  });
});

// Instant open: Impact only opens the drawer once the item is added and the cart is fetched again (about one
// second). We open it as soon as the theme starts the add to cart request (it marks the add to cart button as busy),
// show a loading placeholder, and let the theme fill in the content when it is ready.
function getCartDrawer() {
  return document.getElementById('cart-drawer');
}

function stopAdding(drawer) {
  clearTimeout(drawer.addingTimeout);
  drawer.classList.remove('is-adding');
}

function showDrawerAlert(drawer, message) {
  drawer.querySelector('.cdp-alert')?.remove();

  const anchor = drawer.querySelector('.cdp-progress, .cdp-items, .cdp-empty');
  if (!anchor || !message) return;

  const alert = document.createElement('p');
  alert.className = 'cdp-alert';
  alert.setAttribute('role', 'alert');
  alert.textContent = message;
  anchor.before(alert);
}

function startAdding(form) {
  const settings = window.themeVariables?.settings || {};
  const drawer = getCartDrawer();

  if (!drawer || settings.cartType !== 'drawer' || settings.pageType === 'cart' || drawer.classList.contains('is-adding')) return;

  // Start loading the upsells for this product in parallel with the add to cart request
  fetchRecommendations(form.elements['product-id']?.value);

  // When adding from a quick buy drawer, close it right away so it does not cover the cart
  form.closest('quick-buy-drawer')?.hide?.();

  drawer.querySelector('.cdp-alert')?.remove();
  drawer.pendingAlert = null;
  drawer.classList.add('is-adding');
  drawer.addingTimeout = setTimeout(() => stopAdding(drawer), 8000);
  drawer.show();
}

// The theme's product form sets aria-busy on its submit buttons right before sending the add to cart request (only
// once the form is valid and not blocked by an app, e.g. a required upload), which makes it a reliable start signal.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const button = mutation.target;

    if (button.type !== 'submit' || button.getAttribute('aria-busy') !== 'true') continue;

    const form = button.form;

    if (form?.getAttribute('is') === 'product-form' && form.action.includes('/cart/add')) {
      startAdding(form);
      break;
    }
  }
}).observe(document.documentElement, { attributes: true, attributeFilter: ['aria-busy'], subtree: true });

// The theme replaces the drawer content on every cart change and on refresh: that's when loading is over
function watchDrawer() {
  const drawer = getCartDrawer();
  if (!drawer || drawer.cdpObserved) return;

  drawer.cdpObserved = true;

  new MutationObserver(() => {
    stopAdding(drawer);

    if (drawer.pendingAlert) {
      showDrawerAlert(drawer, drawer.pendingAlert);
      drawer.pendingAlert = null;
    }
  }).observe(drawer, { childList: true });
}

watchDrawer();
document.addEventListener('shopify:section:load', watchDrawer);

// Shopify can refuse part of an add (e.g. "Only 2 items were added to your cart due to availability."). Keep the
// drawer open: the theme refreshes it with what was really added, and we show Shopify's message on top.
document.addEventListener('cart:error', (event) => {
  const drawer = getCartDrawer();
  if (!drawer?.classList.contains('is-adding')) return;

  drawer.pendingAlert = event.detail?.error || event.detail?.message || '';
  showDrawerAlert(drawer, drawer.pendingAlert);
});

// Warm up the upsells on product pages, so they are ready before the customer adds to cart
const currentProductId = window.ShopifyAnalytics?.meta?.product?.id;

if (currentProductId) {
  (window.requestIdleCallback || ((callback) => setTimeout(callback, 1500)))(() => fetchRecommendations(String(currentProductId)));
}

if (!window.customElements.get('cart-upsell-item')) {
  window.customElements.define('cart-upsell-item', CartUpsellItem);
}

if (!window.customElements.get('cart-upsells')) {
  window.customElements.define('cart-upsells', CartUpsells);
}

if (!window.customElements.get('cart-addon-toggle')) {
  window.customElements.define('cart-addon-toggle', CartAddonToggle);
}
